import React from "react";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import Button from "../ui/Button";
import Figure from "../ui/Figure";
import { Accent, Display, Label } from "../ui/Type";
import { SERVICE_AREAS } from "../../data/company";
import { color, motion, space, type } from "../../design/tokens";

/**
 * Homepage hero.
 *
 * An ink field carrying an oversized serif statement, with a tall portrait
 * plate bleeding off the top and right edges and a square detail plate
 * overlapping it. The fixed header sits transparent over the top of it.
 *
 * The composition is portrait-led because the source photography is: these are
 * phone photos at 4:5 and 1:1. A conventional full-bleed 16:9 hero would mean
 * cropping the staircase down to a band of its middle steps, which is exactly
 * the compromise that makes contractor sites look like they bought their
 * pictures. Framing the photo tall keeps it whole and, as it happens, gives
 * the type far more room than a centred-over-image hero ever does.
 *
 * Nothing above the fold is wrapped in a scroll reveal — reveals depend on an
 * observer callback that fires after first paint, and an empty hero for even
 * one frame is worse than no animation at all.
 */
const Hero = () => (
  <Box
    component="section"
    aria-labelledby="hero-heading"
    sx={{
      position: "relative",
      backgroundColor: color.ink,
      color: color.onInk,
      overflow: "hidden",
      minHeight: { xs: "auto", md: "100vh" },
      "@supports (height: 100svh)": { minHeight: { xs: "auto", md: "100svh" } },
      // Column, not row. The service-area rail below is `position: absolute`
      // on desktop but static on mobile, and as a row-flex sibling it would
      // sit *beside* the hero content on a phone rather than under it.
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
    }}
  >
    <Box
      sx={{
        width: "100%",
        maxWidth: space.maxWidth,
        marginInline: "auto",
        paddingInline: space.gutter,
        // Takes the full height of the column so the content stays optically
        // centred in the viewport on desktop.
        flexGrow: 1,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
        alignItems: "center",
        columnGap: { md: 6, lg: 10 },
        // Clears the fixed header; the extra bottom padding leaves room for
        // the meta rail pinned along the base on desktop.
        paddingTop: { xs: "104px", md: "136px" },
        paddingBottom: { xs: 8, md: "148px" },
      }}
    >
      {/* ── Statement ─────────────────────────────────────────────────── */}
      <Box sx={{ order: { xs: 2, md: 1 }, paddingTop: { xs: 6, md: 0 } }}>
        <Label tone="onInk">Five Lights Reno</Label>

        <Display
          as="h1"
          size="xl"
          tone="onInk"
          id="hero-heading"
          sx={{ marginTop: { xs: 3, md: 4 } }}
        >
          {/* The trailing space matters: JSX drops the newline before <br/>,
              so without it the accessible name and the text a crawler reads
              come out as "changesthe way". */}
          Renovation that changes{" "}
          <br />
          the way your home{" "}
          <Accent sx={{ color: color.clayLight }}>feels</Accent>.
        </Display>

        <Box
          component="p"
          sx={{
            ...type.lead,
            color: color.onInkMute,
            maxWidth: 480,
            marginTop: { xs: 3, md: 4 },
            marginBottom: 0,
          }}
        >
          Kitchens, bathrooms, legal basements and full home renovations —
          planned around how you actually live in the space, and built by one
          crew from the first walkthrough to the final walkthrough.
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            marginTop: { xs: 4, md: 5 },
          }}
        >
          <Button to="/contact" variant="onInk" size="lg">
            Get a Free Quote
          </Button>
          <Button to="/projects" variant="onInkGhost" size="lg">
            Explore Our Projects
          </Button>
        </Box>
      </Box>

      {/* ── Photography ───────────────────────────────────────────────── */}
      <Box
        sx={{
          order: { xs: 1, md: 2 },
          position: "relative",
          alignSelf: { md: "stretch" },
          display: "flex",
          alignItems: "center",
          minHeight: { md: 480 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            // Bleeds past the container's right gutter on large screens so the
            // plate runs off the edge of the page rather than sitting in a box.
            marginRight: { lg: `calc(${space.gutter} * -1)` },
          }}
        >
          <Figure
            media="staircaseAfter"
            ratio={{ xs: "4 / 5", md: "3 / 4.2" }}
            priority
            reveal={false}
            tone="onInk"
            caption={false}
            objectPosition="center 35%"
          />
        </Box>

        {/* Overlapping detail plate. Hidden below md — on a phone it would
            cover the primary image rather than counterpoint it. */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            left: { md: -48, lg: -88 },
            bottom: { md: 36, lg: 56 },
            width: { md: 160, lg: 210 },
            border: `1px solid ${color.lineOnInk}`,
          }}
        >
          <Figure
            media="tileFeatureWall"
            ratio="1 / 1"
            reveal={false}
            caption={false}
            tone="onInk"
          />
        </Box>
      </Box>
    </Box>

    {/* ── Service-area rail ─────────────────────────────────────────────
        Pinned to the base of the hero on desktop so the geography lands in
        the first screenful; inline on mobile where there's no room to pin. */}
    <Box
      sx={{
        position: { xs: "static", md: "absolute" },
        left: 0,
        right: 0,
        bottom: 0,
        borderTop: `1px solid ${color.lineOnInk}`,
        backgroundColor: color.ink,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: space.maxWidth,
          marginInline: "auto",
          paddingInline: space.gutter,
          paddingBlock: { xs: 3, md: 3.5 },
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: { xs: 1.5, md: 3 },
          }}
        >
          {SERVICE_AREAS.map((area, index) => (
            <Box
              component="li"
              key={area.slug}
              sx={{ display: "inline-flex", alignItems: "center", gap: { xs: 1.5, md: 3 } }}
            >
              {index > 0 ? (
                <Box component="span" aria-hidden="true" sx={{ color: color.lineOnInk }}>
                  ·
                </Box>
              ) : null}
              <Box
                component={Link}
                to={`/${area.slug}`}
                sx={{
                  ...type.label,
                  color: color.onInkMute,
                  // An 11px label needs vertical padding to clear the WCAG 2.2
                  // 24px minimum target size.
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 28,
                  transition: `color ${motion.base} ${motion.ease}`,
                  "&:hover": { color: color.onInk },
                }}
              >
                {area.name}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          component="p"
          sx={{ ...type.caption, color: color.onInkMute, margin: 0 }}
        >
          Based in Pickering, Ontario
        </Box>
      </Box>
    </Box>
  </Box>
);

export default Hero;
